import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionContext';
import type { PermissionAction, PermissionResource } from '../types/domain';

/**
 * Route guard: requires a real Supabase Auth session (redirects to /login
 * otherwise). Optionally also requires a specific server-resolved
 * permission — if the user is authenticated but `fn_check_permission`
 * (via PermissionContext) says no, this renders an explicit "access
 * restricted" panel rather than the page, and never renders the page and
 * then hopes the API calls inside it fail safely. This is UX-layer defense
 * in depth only: the permission-gated RPC functions themselves are what
 * actually prevent a denied mutation, not this component.
 *
 * STATUS: IMPLEMENTED_UNVERIFIED — see docs/data-dictionary.md §8k.
 */
export function ProtectedRoute({
  children,
  requirePermission,
}: {
  children: ReactNode;
  requirePermission?: { resource: PermissionResource; action: PermissionAction };
}) {
  const { session, loading: authLoading } = useAuth();
  const { hasPermission, loading: permLoading } = usePermissions();

  if (authLoading) {
    return <CenteredNote text="Memeriksa sesi..." />;
  }
  if (!session) {
    return <Navigate to="/login" replace />;
  }
  if (requirePermission) {
    if (permLoading) {
      return <CenteredNote text="Memeriksa izin akses..." />;
    }
    if (!hasPermission(requirePermission.resource, requirePermission.action)) {
      return (
        <div
          className="alex-card"
          style={{ padding: 32, maxWidth: 520, margin: '48px auto', textAlign: 'center' }}
        >
          <div style={{ fontSize: 32, marginBottom: 10 }}>🔒</div>
          <h2 style={{ margin: '0 0 8px', color: 'var(--alex-text-primary)', fontSize: 'var(--alex-fs-section-title)' }}>
            Akses dibatasi
          </h2>
          <p style={{ color: 'var(--alex-text-secondary)', fontSize: 'var(--alex-fs-body)', margin: 0 }}>
            Role Anda tidak memiliki izin <code>{requirePermission.resource}.{requirePermission.action}</code> —
            diverifikasi server-side lewat <code>fn_check_permission()</code> (migration 0032), bukan hanya
            disembunyikan di tampilan.
          </p>
        </div>
      );
    }
  }
  return <>{children}</>;
}

function CenteredNote({ text }: { text: string }) {
  return (
    <div style={{ minHeight: '40vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--alex-text-muted)' }}>
      {text}
    </div>
  );
}
